<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import {
  SOURCES,
  STATUS_BADGE_CLASSES,
  fetchJson,
  normalizeTicimax,
  normalizeShopify,
  normalizeTrendyol,
  formatDate,
  formatCurrency,
} from '../helpers/orderHelpers';
import { fetchMarketplaceJson as fetchMarketplace } from 'dashboard/helpers/marketplaceApi';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const fetchMarketplaceJson = path =>
  fetchMarketplace(route.params.accountId, path);

// --- Tüm filtreleme SUNUCU tarafında yapılır -------------------------------
// Eskiden durum filtresi yalnızca ekrandaki 20 satıra uygulanıyordu; "İptal"
// seçince tüm iptaller değil, o sayfada rastlanan birkaç tanesi görünüyordu.
// Artık her filtre marketplace_orders üzerinde çalışır, üstteki özet de
// filtrelenmiş KÜMENİN tamamını sayar.
const PAGE_SIZES = [20, 50, 100];

const CHANNEL_TABS = [
  { key: 'all', label: 'Tümü' },
  { key: 'sites', label: 'Tüm Siteler' },
  { key: 'ticimax', label: 'Ticimax · Koza' },
  { key: 'trendyol_koza', label: 'Trendyol · Koza' },
  { key: 'shopify', label: 'Shopify · Modax' },
  { key: 'trendyol_modax', label: 'Trendyol · Modax' },
];
const CHANNEL_PARAM = { sites: 'ticimax,shopify' };

const AGENT_ANY = '__any__';
const AGENT_NONE = '__none__';

const isLoading = ref(true);
const isRefreshing = ref(false);
const loadError = ref('');
const orders = ref([]);
const total = ref(0);
const summary = ref({ count: 0, gross: 0, buckets: {} });
const facets = ref({
  agents: { list: [], withAgent: 0, withoutAgent: 0 },
  statuses: [],
  payments: {},
});

const channelFilter = ref('all');
const searchQuery = ref('');
const filterOrderNo = ref('');
const filterCustomer = ref('');
const filterAgent = ref('');
const filterStatus = ref('');
const filterStatusRaw = ref('');
const filterPayment = ref('');
const filterMin = ref('');
const filterMax = ref('');
const datePreset = ref('all');
const customStart = ref('');
const customEnd = ref('');
const sortBy = ref('date_desc');
const pageSize = ref(20);
const page = ref(1);

const activeHeaderFilter = ref(null);
const toggleHeaderFilter = key => {
  activeHeaderFilter.value = activeHeaderFilter.value === key ? null : key;
};

const DATE_PRESETS = [
  { v: 'all', l: t('ORDERS.DATE_PRESETS.ALL') },
  { v: 'today', l: t('ORDERS.DATE_PRESETS.TODAY') },
  { v: 'yesterday', l: t('ORDERS.DATE_PRESETS.YESTERDAY') },
  { v: 'last7', l: t('ORDERS.DATE_PRESETS.LAST_7') },
  { v: 'last30', l: t('ORDERS.DATE_PRESETS.LAST_30') },
  { v: 'this_month', l: t('ORDERS.DATE_PRESETS.THIS_MONTH') },
  { v: 'last_month', l: t('ORDERS.DATE_PRESETS.LAST_MONTH') },
];

const SORT_OPTIONS = [
  { v: 'date_desc', l: t('ORDERS.SORT.DATE_DESC') },
  { v: 'date_asc', l: t('ORDERS.SORT.DATE_ASC') },
  { v: 'amount_desc', l: t('ORDERS.SORT.AMOUNT_DESC') },
  { v: 'amount_asc', l: t('ORDERS.SORT.AMOUNT_ASC') },
];

const PAYMENT_OPTIONS = [
  { v: 'cod', l: t('ORDERS.PAYMENT_FILTER.COD') },
  { v: 'online', l: t('ORDERS.PAYMENT_FILTER.ONLINE') },
  { v: 'transfer', l: t('ORDERS.PAYMENT_FILTER.TRANSFER') },
  { v: 'other', l: t('ORDERS.PAYMENT_FILTER.OTHER') },
];

const STATUS_BUCKETS = [
  { v: 'success', l: t('ORDERS.STATUS_FILTER.SUCCESS') },
  { v: 'pending', l: t('ORDERS.STATUS_FILTER.PENDING') },
  { v: 'cancelled', l: t('ORDERS.STATUS_FILTER.CANCELLED') },
];

const RANGE_ARROW = '→';

const pad = n => String(n).padStart(2, '0');
const isoDay = d =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Tarih kısayolları tarayıcının yerel gününe göre çözülür; sunucu bunları
// Europe/Istanbul gün başı/sonu olarak yorumlar (controller#date_range).
const resolveRange = () => {
  const today = new Date();
  const start = new Date(today);
  switch (datePreset.value) {
    case 'today':
      return [isoDay(today), isoDay(today)];
    case 'yesterday':
      start.setDate(start.getDate() - 1);
      return [isoDay(start), isoDay(start)];
    case 'last7':
      start.setDate(start.getDate() - 6);
      return [isoDay(start), isoDay(today)];
    case 'last30':
      start.setDate(start.getDate() - 29);
      return [isoDay(start), isoDay(today)];
    case 'this_month':
      start.setDate(1);
      return [isoDay(start), isoDay(today)];
    case 'last_month': {
      const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const to = new Date(today.getFullYear(), today.getMonth(), 0);
      return [isoDay(from), isoDay(to)];
    }
    case 'custom':
      return customStart.value && customEnd.value
        ? [customStart.value, customEnd.value]
        : null;
    default:
      return null;
  }
};

const buildParams = () => {
  const params = new URLSearchParams({
    limit: String(pageSize.value),
    offset: String((page.value - 1) * pageSize.value),
    sort: sortBy.value,
  });
  const channelParam =
    CHANNEL_PARAM[channelFilter.value] || channelFilter.value;
  if (channelParam !== 'all') params.set('channel', channelParam);
  if (searchQuery.value.trim()) params.set('q', searchQuery.value.trim());
  if (filterOrderNo.value.trim())
    params.set('order_no', filterOrderNo.value.trim());
  if (filterCustomer.value.trim())
    params.set('customer', filterCustomer.value.trim());
  if (filterAgent.value) params.set('agent', filterAgent.value);
  if (filterStatus.value) params.set('status', filterStatus.value);
  if (filterStatusRaw.value) params.set('status_raw', filterStatusRaw.value);
  if (filterPayment.value) params.set('payment', filterPayment.value);
  if (filterMin.value) params.set('min_amount', String(filterMin.value));
  if (filterMax.value) params.set('max_amount', String(filterMax.value));
  const range = resolveRange();
  if (range) {
    params.set('start', range[0]);
    params.set('end', range[1]);
  }
  return params;
};

const normalizeRow = item => {
  const { channel, order, commissionAmount } = item;
  let normalized;
  if (channel === 'shopify') normalized = normalizeShopify(order);
  else if (channel === 'trendyol_koza' || channel === 'trendyol_modax')
    normalized = normalizeTrendyol(order, channel, commissionAmount);
  else normalized = normalizeTicimax(order);
  return { ...normalized, agentName: item.agentName || '' };
};

let loadToken = 0;

const load = async () => {
  loadToken += 1;
  const token = loadToken;
  if (orders.value.length) isRefreshing.value = true;
  else isLoading.value = true;
  loadError.value = '';
  try {
    const data = await fetchMarketplaceJson(`orders?${buildParams()}`);
    if (token !== loadToken) return;
    orders.value = (data.orders || []).map(normalizeRow);
    total.value = data.total || 0;
    summary.value = data.summary || { count: 0, gross: 0, buckets: {} };
    facets.value = data.facets || facets.value;
  } catch (error) {
    if (token !== loadToken) return;
    loadError.value = t('ORDERS.ERRORS.BOTH_FAILED');
    orders.value = [];
    total.value = 0;
  } finally {
    if (token === loadToken) {
      isLoading.value = false;
      isRefreshing.value = false;
    }
  }
};

let debounceTimer = null;
const reload = ({ resetPage = true, debounce = 0 } = {}) => {
  if (resetPage) page.value = 1;
  clearTimeout(debounceTimer);
  if (!debounce) {
    load();
    return;
  }
  debounceTimer = setTimeout(load, debounce);
};

onMounted(load);

// Yazılan filtreler beklemeli, seçilenler anında uygulanır.
watch([searchQuery, filterOrderNo, filterCustomer, filterMin, filterMax], () =>
  reload({ debounce: 400 })
);
watch(
  [
    channelFilter,
    filterAgent,
    filterStatus,
    filterStatusRaw,
    filterPayment,
    datePreset,
    customStart,
    customEnd,
    sortBy,
    pageSize,
  ],
  () => reload()
);

const totalPages = computed(() =>
  Math.max(1, Math.ceil(total.value / pageSize.value))
);
const goToPage = target => {
  if (target < 1 || target > totalPages.value || target === page.value) return;
  page.value = target;
  load();
};

const activeFilters = computed(() =>
  [
    searchQuery.value.trim(),
    filterOrderNo.value.trim(),
    filterCustomer.value.trim(),
    filterAgent.value,
    filterStatus.value,
    filterStatusRaw.value,
    filterPayment.value,
    filterMin.value,
    filterMax.value,
    datePreset.value !== 'all' ? datePreset.value : '',
  ].filter(Boolean)
);

const clearFilters = () => {
  searchQuery.value = '';
  filterOrderNo.value = '';
  filterCustomer.value = '';
  filterAgent.value = '';
  filterStatus.value = '';
  filterStatusRaw.value = '';
  filterPayment.value = '';
  filterMin.value = '';
  filterMax.value = '';
  datePreset.value = 'all';
  customStart.value = '';
  customEnd.value = '';
};

const pickAgent = value => {
  filterAgent.value = value;
  activeHeaderFilter.value = null;
};

const pickStatus = (bucket, raw) => {
  filterStatus.value = bucket;
  filterStatusRaw.value = raw;
  activeHeaderFilter.value = null;
};

// --- Derin arama: kanalın kendi geçmişi ------------------------------------
// Sistemdeki kayıtlar Ticimax'ta 2026-01, Shopify'da 2024-09 tarihinde başlıyor.
// Daha eskisi arandığında kanal API'lerine gidilir; yavaştır ve Trendyol'u
// kapsamaz, bu yüzden ana arama değil BİLİNÇLİ ikinci adımdır.
const deepResults = ref([]);
const isDeepSearching = ref(false);
const hasDeepSearched = ref(false);
const deepError = ref('');

const looksLikeOrderNumber = value => /^#?\d{3,8}$/.test(value.trim());

const normalizeForCustomerSearch = value => {
  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/g, '');
  return /^[+\d][\d\s().-]*$/.test(trimmed) && digits.length >= 7
    ? digits
    : trimmed;
};

const searchShopify = async query => {
  const trimmed = query.trim();
  const found = new Map();

  if (looksLikeOrderNumber(trimmed)) {
    const name = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    const data = await fetchJson(
      `/shopify-api/orders.json?status=any&name=${encodeURIComponent(name)}`
    );
    (data.orders || []).forEach(order => found.set(order.id, order));
    return Array.from(found.values()).map(normalizeShopify);
  }

  const customersData = await fetchJson(
    `/shopify-api/customers/search.json?query=${encodeURIComponent(normalizeForCustomerSearch(trimmed))}&limit=15`
  );
  const orderLists = await Promise.all(
    (customersData.customers || []).map(customer =>
      fetchJson(
        `/shopify-api/customers/${customer.id}/orders.json?status=any`
      ).catch(() => ({ orders: [] }))
    )
  );
  orderLists.forEach(({ orders: customerOrders }) =>
    (customerOrders || []).forEach(order => found.set(order.id, order))
  );
  return Array.from(found.values()).map(normalizeShopify);
};

const runDeepSearch = async () => {
  const query = searchQuery.value.trim();
  if (!query) return;

  isDeepSearching.value = true;
  hasDeepSearched.value = true;
  deepError.value = '';
  deepResults.value = [];

  const [ticimaxResult, shopifyResult] = await Promise.allSettled([
    fetchJson(`/ticimax-api/siparisler?q=${encodeURIComponent(query)}`),
    searchShopify(query),
  ]);

  const merged = [];
  if (ticimaxResult.status === 'fulfilled')
    merged.push(...(ticimaxResult.value.orders || []).map(normalizeTicimax));
  if (shopifyResult.status === 'fulfilled') merged.push(...shopifyResult.value);
  merged.sort((a, b) => new Date(b.date) - new Date(a.date));
  deepResults.value = merged;

  if (
    ticimaxResult.status === 'rejected' &&
    shopifyResult.status === 'rejected'
  ) {
    deepError.value = t('ORDERS.ERRORS.BOTH_FAILED');
  }
  isDeepSearching.value = false;
};

const clearDeepSearch = () => {
  hasDeepSearched.value = false;
  deepResults.value = [];
  deepError.value = '';
};

const isDeepMode = computed(() => hasDeepSearched.value);
const visibleOrders = computed(() =>
  isDeepMode.value ? deepResults.value : orders.value
);

const openOrder = order => {
  const isTrendyol =
    order.source === 'trendyol_koza' || order.source === 'trendyol_modax';
  router.push({
    name: 'orders_detail',
    params: { source: order.source, orderId: order.id },
    ...(isTrendyol
      ? {
          state: {
            rawOrder: JSON.stringify(order.raw),
            correctedCommission: order.correctedCommission,
          },
        }
      : {}),
  });
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center justify-between gap-4 px-4 py-4 border-b sm:px-6 border-n-weak flex-shrink-0"
    >
      <div class="min-w-0">
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ t('ORDERS.TITLE') }}
        </h1>
        <p class="text-sm truncate text-n-slate-11">
          {{ t('ORDERS.SUBTITLE') }}
        </p>
      </div>
      <button
        type="button"
        class="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg text-n-slate-11 outline outline-1 outline-n-weak hover:bg-n-alpha-1 transition-colors flex-shrink-0"
        :disabled="isLoading"
        @click="load"
      >
        <span
          class="size-4 i-lucide-refresh-cw"
          :class="{ 'animate-spin': isLoading || isRefreshing }"
        />
        <span class="hidden sm:inline">{{ t('ORDERS.REFRESH') }}</span>
      </button>
    </header>

    <!-- Kanal filtre sekmeleri -->
    <div
      class="flex items-center gap-1 px-4 sm:px-6 pt-3 pb-0 border-b border-n-weak flex-shrink-0 overflow-x-auto"
    >
      <button
        v-for="tab in CHANNEL_TABS"
        :key="tab.key"
        class="px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors"
        :class="
          channelFilter === tab.key
            ? 'border-woot-500 text-woot-600 dark:text-woot-400'
            : 'border-transparent text-n-slate-10 hover:text-n-slate-12 hover:border-n-slate-6'
        "
        @click="channelFilter = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Arama + özet + sıralama -->
    <section
      class="flex flex-col gap-3 px-4 py-3 border-b sm:px-6 border-n-weak flex-shrink-0"
    >
      <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div class="relative flex-1 min-w-0">
          <span
            class="absolute -translate-y-1/2 size-4 i-lucide-search left-3 top-1/2 text-n-slate-10"
          />
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('ORDERS.SEARCH.PLACEHOLDER')"
            class="w-full py-2 pl-9 pr-8 text-sm border rounded-lg bg-n-alpha-1 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
          <button
            v-if="searchQuery"
            type="button"
            class="absolute -translate-y-1/2 size-4 i-lucide-x right-3 top-1/2 text-n-slate-10 hover:text-n-slate-12"
            @click="searchQuery = ''"
          />
        </div>
        <!-- Chatwoot'un temel form stili select'e width:100% veriyor; sabit
             genislik verilmezse arama kutusunu ezip sifira dusuruyor. -->
        <select
          v-model="sortBy"
          class="flex-shrink-0 w-full px-3 py-2 text-sm border rounded-lg sm:w-56 bg-n-alpha-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        >
          <option v-for="opt in SORT_OPTIONS" :key="opt.v" :value="opt.v">
            {{ opt.l }}
          </option>
        </select>
      </div>

      <!-- Filtrelenmiş kümenin karnesi (ekrandaki sayfanın değil) -->
      <div class="flex flex-wrap items-center gap-2 text-xs">
        <span
          class="px-2.5 py-1 font-medium rounded-full bg-n-alpha-2 text-n-slate-12 tabular-nums"
        >
          {{ t('ORDERS.SUMMARY.ORDERS', { count: summary.count }) }}
        </span>
        <span
          class="px-2.5 py-1 font-medium rounded-full bg-n-alpha-2 text-n-slate-12 tabular-nums"
        >
          {{
            t('ORDERS.SUMMARY.REVENUE', {
              amount: formatCurrency(summary.gross),
            })
          }}
        </span>
        <button
          v-for="bucket in STATUS_BUCKETS"
          :key="bucket.v"
          type="button"
          class="px-2.5 py-1 font-medium rounded-full transition-opacity hover:opacity-80 tabular-nums"
          :class="[
            STATUS_BADGE_CLASSES[bucket.v],
            filterStatus === bucket.v ? 'ring-2 ring-n-brand' : '',
          ]"
          @click="pickStatus(filterStatus === bucket.v ? '' : bucket.v, '')"
        >
          {{ bucket.l }}
          <b>{{ (summary.buckets || {})[bucket.v] || 0 }}</b>
        </button>
        <button
          v-if="activeFilters.length"
          type="button"
          class="flex items-center gap-1 px-2.5 py-1 ml-auto rounded-lg text-n-slate-11 hover:bg-n-alpha-1"
          @click="clearFilters"
        >
          <span class="size-3.5 i-lucide-filter-x" />
          {{ t('ORDERS.FILTERS.CLEAR') }}
        </button>
        <span v-else class="ml-auto text-n-slate-10">
          {{ t('ORDERS.FILTERS.HINT') }}
        </span>
      </div>

      <!-- Derin arama: yalnızca arama kutusu doluyken teklif edilir -->
      <div
        v-if="searchQuery.trim()"
        class="flex flex-wrap items-center gap-2 text-xs text-n-slate-10"
      >
        <button
          type="button"
          class="flex items-center gap-1.5 px-2.5 py-1 rounded-lg outline outline-1 outline-n-weak text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50"
          :disabled="isDeepSearching"
          @click="runDeepSearch"
        >
          <span
            v-if="isDeepSearching"
            class="size-3.5 i-lucide-loader-circle animate-spin"
          />
          <span v-else class="size-3.5 i-lucide-history" />
          {{ t('ORDERS.DEEP_SEARCH.BUTTON') }}
        </button>
        <span v-if="isDeepSearching">{{
          t('ORDERS.DEEP_SEARCH.SEARCHING')
        }}</span>
        <template v-else-if="isDeepMode">
          <span class="text-n-amber-11">
            {{ t('ORDERS.DEEP_SEARCH.RESULTS', { count: deepResults.length }) }}
          </span>
          <button
            type="button"
            class="underline hover:text-n-slate-12"
            @click="clearDeepSearch"
          >
            {{ t('ORDERS.DEEP_SEARCH.CLEAR') }}
          </button>
        </template>
        <span v-else>{{ t('ORDERS.DEEP_SEARCH.HINT') }}</span>
        <span v-if="deepError" class="text-n-ruby-10">{{ deepError }}</span>
      </div>
    </section>

    <div class="relative flex-1 overflow-y-auto">
      <!-- Açık filtre menüsünü kapatmak için saydam arka plan. z-20 OLMALI:
           sticky thead z-30 ile kendi yığın bağlamını kuruyor, arka plan bunun
           üstünde kalırsa menüdeki seçenekler tıklanamaz hale geliyor. -->
      <div
        v-if="activeHeaderFilter"
        class="fixed inset-0 z-20"
        @click="activeHeaderFilter = null"
      />

      <div
        v-if="isLoading"
        class="flex flex-col items-center justify-center h-full gap-3 text-n-slate-11"
      >
        <Spinner />
        <p class="text-sm">{{ t('ORDERS.LOADING') }}</p>
      </div>

      <div
        v-else-if="loadError && !visibleOrders.length"
        class="flex flex-col items-center justify-center h-full gap-3 text-n-slate-11"
      >
        <span class="size-9 i-lucide-alert-triangle text-n-ruby-9" />
        <p class="text-sm">{{ loadError }}</p>
        <button
          type="button"
          class="px-3 py-1.5 text-sm font-medium rounded-lg outline outline-1 outline-n-weak hover:bg-n-alpha-1"
          @click="load"
        >
          {{ t('ORDERS.RETRY') }}
        </button>
      </div>

      <table v-else class="w-full text-sm border-collapse">
        <thead
          class="sticky top-0 z-30 text-xs tracking-wide uppercase border-b bg-n-background border-n-weak text-n-slate-10"
        >
          <tr>
            <!-- Sipariş No -->
            <th class="relative px-4 py-2.5 text-start font-medium sm:px-6">
              <span class="inline-flex items-center gap-1">
                {{ t('ORDERS.COLUMNS.ORDER_NO') }}
                <button
                  type="button"
                  class="text-sm"
                  :class="
                    filterOrderNo
                      ? 'i-lucide-filter text-woot-500'
                      : 'i-lucide-filter text-n-slate-9 hover:text-n-slate-11'
                  "
                  @click.stop="toggleHeaderFilter('orderNo')"
                />
              </span>
              <div
                v-if="activeHeaderFilter === 'orderNo'"
                class="absolute z-50 p-2 mt-1 border rounded-lg shadow-lg left-4 top-full bg-n-background border-n-weak w-56"
                @click.stop
              >
                <input
                  v-model="filterOrderNo"
                  type="text"
                  :placeholder="t('ORDERS.FILTERS.ORDER_NO_PLACEHOLDER')"
                  class="w-full px-2 py-1.5 text-sm normal-case border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12"
                />
              </div>
            </th>

            <!-- Müşteri -->
            <th class="relative px-4 py-2.5 text-start font-medium">
              <span class="inline-flex items-center gap-1">
                {{ t('ORDERS.COLUMNS.CUSTOMER') }}
                <button
                  type="button"
                  class="text-sm"
                  :class="
                    filterCustomer
                      ? 'i-lucide-filter text-woot-500'
                      : 'i-lucide-filter text-n-slate-9 hover:text-n-slate-11'
                  "
                  @click.stop="toggleHeaderFilter('customer')"
                />
              </span>
              <div
                v-if="activeHeaderFilter === 'customer'"
                class="absolute z-50 p-2 mt-1 border rounded-lg shadow-lg left-4 top-full bg-n-background border-n-weak w-60"
                @click.stop
              >
                <input
                  v-model="filterCustomer"
                  type="text"
                  :placeholder="t('ORDERS.FILTERS.CUSTOMER_PLACEHOLDER')"
                  class="w-full px-2 py-1.5 text-sm normal-case border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12"
                />
              </div>
            </th>

            <!-- Temsilci -->
            <th class="relative px-4 py-2.5 text-start font-medium">
              <span class="inline-flex items-center gap-1">
                {{ t('ORDERS.COLUMNS.AGENT') }}
                <button
                  type="button"
                  class="text-sm"
                  :class="
                    filterAgent
                      ? 'i-lucide-filter text-woot-500'
                      : 'i-lucide-filter text-n-slate-9 hover:text-n-slate-11'
                  "
                  @click.stop="toggleHeaderFilter('agent')"
                />
              </span>
              <div
                v-if="activeHeaderFilter === 'agent'"
                class="absolute z-50 p-1 mt-1 overflow-auto normal-case border rounded-lg shadow-lg left-4 top-full bg-n-background border-n-weak w-64 max-h-80"
                @click.stop
              >
                <p class="px-2 py-1.5 text-[11px] leading-snug text-n-slate-10">
                  {{ t('ORDERS.AGENT_HINT') }}
                </p>
                <button
                  type="button"
                  class="block w-full px-2 py-1.5 text-xs text-left rounded-md hover:bg-n-alpha-1"
                  :class="
                    !filterAgent
                      ? 'text-woot-500 font-semibold'
                      : 'text-n-slate-11'
                  "
                  @click="pickAgent('')"
                >
                  {{ t('ORDERS.FILTERS.ALL') }}
                </button>
                <button
                  type="button"
                  class="flex items-center justify-between w-full px-2 py-1.5 text-xs text-left rounded-md hover:bg-n-alpha-1"
                  :class="
                    filterAgent === AGENT_ANY
                      ? 'text-woot-500 font-semibold'
                      : 'text-n-slate-11'
                  "
                  @click="pickAgent(AGENT_ANY)"
                >
                  <span>{{ t('ORDERS.FILTERS.AGENT_ANY') }}</span>
                  <b class="tabular-nums">{{ facets.agents.withAgent }}</b>
                </button>
                <button
                  type="button"
                  class="flex items-center justify-between w-full px-2 py-1.5 text-xs text-left rounded-md hover:bg-n-alpha-1"
                  :class="
                    filterAgent === AGENT_NONE
                      ? 'text-woot-500 font-semibold'
                      : 'text-n-slate-11'
                  "
                  @click="pickAgent(AGENT_NONE)"
                >
                  <span>{{ t('ORDERS.FILTERS.AGENT_NONE') }}</span>
                  <b class="tabular-nums">{{ facets.agents.withoutAgent }}</b>
                </button>
                <div class="my-1 border-t border-n-weak" />
                <button
                  v-for="agent in facets.agents.list"
                  :key="agent.value"
                  type="button"
                  class="flex items-center justify-between w-full px-2 py-1.5 text-xs text-left rounded-md hover:bg-n-alpha-1"
                  :class="
                    filterAgent === agent.value
                      ? 'text-woot-500 font-semibold'
                      : 'text-n-slate-11'
                  "
                  @click="pickAgent(agent.value)"
                >
                  <span class="truncate">{{ agent.value }}</span>
                  <b class="ml-2 tabular-nums">{{ agent.count }}</b>
                </button>
              </div>
            </th>

            <!-- Tutar -->
            <th class="relative px-4 py-2.5 text-start font-medium">
              <span class="inline-flex items-center gap-1">
                {{ t('ORDERS.COLUMNS.AMOUNT') }}
                <button
                  type="button"
                  class="text-sm"
                  :class="
                    filterMin || filterMax
                      ? 'i-lucide-filter text-woot-500'
                      : 'i-lucide-filter text-n-slate-9 hover:text-n-slate-11'
                  "
                  @click.stop="toggleHeaderFilter('amount')"
                />
              </span>
              <div
                v-if="activeHeaderFilter === 'amount'"
                class="absolute z-50 flex items-center gap-1.5 p-2 mt-1 normal-case border rounded-lg shadow-lg left-4 top-full bg-n-background border-n-weak w-56"
                @click.stop
              >
                <input
                  v-model="filterMin"
                  type="number"
                  :placeholder="t('ORDERS.FILTERS.AMOUNT_MIN')"
                  class="w-full px-2 py-1 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12"
                />
                <span class="text-xs text-n-slate-10">{{ RANGE_ARROW }}</span>
                <input
                  v-model="filterMax"
                  type="number"
                  :placeholder="t('ORDERS.FILTERS.AMOUNT_MAX')"
                  class="w-full px-2 py-1 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12"
                />
              </div>
            </th>

            <!-- Tarih -->
            <th
              class="relative hidden px-4 py-2.5 text-start font-medium sm:table-cell"
            >
              <span class="inline-flex items-center gap-1">
                {{ t('ORDERS.COLUMNS.DATE') }}
                <button
                  type="button"
                  class="text-sm"
                  :class="
                    datePreset !== 'all'
                      ? 'i-lucide-filter text-woot-500'
                      : 'i-lucide-filter text-n-slate-9 hover:text-n-slate-11'
                  "
                  @click.stop="toggleHeaderFilter('date')"
                />
              </span>
              <div
                v-if="activeHeaderFilter === 'date'"
                class="absolute z-50 flex flex-col gap-2 p-3 mt-1 normal-case border rounded-lg shadow-lg left-4 top-full bg-n-background border-n-weak w-64"
                @click.stop
              >
                <div class="flex items-center gap-1.5">
                  <input
                    v-model="customStart"
                    type="date"
                    class="flex-1 px-2 py-1 text-xs border rounded-md bg-n-solid-1 border-n-weak text-n-slate-12"
                    @change="datePreset = 'custom'"
                  />
                  <span class="text-xs text-n-slate-10">{{ RANGE_ARROW }}</span>
                  <input
                    v-model="customEnd"
                    type="date"
                    class="flex-1 px-2 py-1 text-xs border rounded-md bg-n-solid-1 border-n-weak text-n-slate-12"
                    @change="datePreset = 'custom'"
                  />
                </div>
                <div class="flex flex-wrap gap-1">
                  <button
                    v-for="opt in DATE_PRESETS"
                    :key="opt.v"
                    type="button"
                    class="px-2 py-0.5 text-[11px] rounded-md"
                    :class="
                      datePreset === opt.v
                        ? 'bg-woot-500 text-white'
                        : 'bg-n-solid-2 text-n-slate-11 hover:bg-n-alpha-1'
                    "
                    @click="datePreset = opt.v"
                  >
                    {{ opt.l }}
                  </button>
                </div>
              </div>
            </th>

            <!-- Durum -->
            <th class="relative px-4 py-2.5 text-start font-medium">
              <span class="inline-flex items-center gap-1">
                {{ t('ORDERS.COLUMNS.STATUS') }}
                <button
                  type="button"
                  class="text-sm"
                  :class="
                    filterStatus || filterStatusRaw
                      ? 'i-lucide-filter text-woot-500'
                      : 'i-lucide-filter text-n-slate-9 hover:text-n-slate-11'
                  "
                  @click.stop="toggleHeaderFilter('status')"
                />
              </span>
              <div
                v-if="activeHeaderFilter === 'status'"
                class="absolute z-50 p-1 mt-1 overflow-auto normal-case border rounded-lg shadow-lg left-4 top-full bg-n-background border-n-weak w-64 max-h-80"
                @click.stop
              >
                <button
                  type="button"
                  class="block w-full px-2 py-1.5 text-xs text-left rounded-md hover:bg-n-alpha-1"
                  :class="
                    !filterStatus && !filterStatusRaw
                      ? 'text-woot-500 font-semibold'
                      : 'text-n-slate-11'
                  "
                  @click="pickStatus('', '')"
                >
                  {{ t('ORDERS.STATUS_FILTER.ALL') }}
                </button>
                <button
                  v-for="bucket in STATUS_BUCKETS"
                  :key="bucket.v"
                  type="button"
                  class="block w-full px-2 py-1.5 text-xs text-left rounded-md hover:bg-n-alpha-1"
                  :class="
                    filterStatus === bucket.v
                      ? 'text-woot-500 font-semibold'
                      : 'text-n-slate-11'
                  "
                  @click="pickStatus(bucket.v, '')"
                >
                  {{ bucket.l }}
                </button>
                <p
                  class="px-2 pt-2 pb-1 text-[11px] font-medium text-n-slate-10 border-t border-n-weak mt-1"
                >
                  {{ t('ORDERS.FILTERS.STATUS_EXACT') }}
                </p>
                <button
                  v-for="status in facets.statuses"
                  :key="status.value"
                  type="button"
                  class="flex items-center justify-between w-full px-2 py-1.5 text-xs text-left rounded-md hover:bg-n-alpha-1"
                  :class="
                    filterStatusRaw === status.value
                      ? 'text-woot-500 font-semibold'
                      : 'text-n-slate-11'
                  "
                  @click="pickStatus('', status.value)"
                >
                  <span class="truncate">{{ status.value }}</span>
                  <b class="ml-2 tabular-nums">{{ status.count }}</b>
                </button>
              </div>
            </th>

            <!-- Ödeme -->
            <th
              class="relative hidden px-4 py-2.5 text-start font-medium lg:table-cell"
            >
              <span class="inline-flex items-center gap-1">
                {{ t('ORDERS.COLUMNS.PAYMENT') }}
                <button
                  type="button"
                  class="text-sm"
                  :class="
                    filterPayment
                      ? 'i-lucide-filter text-woot-500'
                      : 'i-lucide-filter text-n-slate-9 hover:text-n-slate-11'
                  "
                  @click.stop="toggleHeaderFilter('payment')"
                />
              </span>
              <div
                v-if="activeHeaderFilter === 'payment'"
                class="absolute z-50 p-1 mt-1 normal-case border rounded-lg shadow-lg right-4 top-full bg-n-background border-n-weak w-56"
                @click.stop
              >
                <button
                  type="button"
                  class="block w-full px-2 py-1.5 text-xs text-left rounded-md hover:bg-n-alpha-1"
                  :class="
                    !filterPayment
                      ? 'text-woot-500 font-semibold'
                      : 'text-n-slate-11'
                  "
                  @click="
                    filterPayment = '';
                    activeHeaderFilter = null;
                  "
                >
                  {{ t('ORDERS.FILTERS.ALL') }}
                </button>
                <button
                  v-for="opt in PAYMENT_OPTIONS"
                  :key="opt.v"
                  type="button"
                  class="flex items-center justify-between w-full px-2 py-1.5 text-xs text-left rounded-md hover:bg-n-alpha-1"
                  :class="
                    filterPayment === opt.v
                      ? 'text-woot-500 font-semibold'
                      : 'text-n-slate-11'
                  "
                  @click="
                    filterPayment = opt.v;
                    activeHeaderFilter = null;
                  "
                >
                  <span>{{ opt.l }}</span>
                  <b class="ml-2 tabular-nums">{{
                    (facets.payments || {})[opt.v] || 0
                  }}</b>
                </button>
              </div>
            </th>

            <th
              class="hidden px-4 py-2.5 text-start font-medium sm:table-cell sm:px-6"
            >
              {{ t('ORDERS.COLUMNS.SOURCE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!visibleOrders.length">
            <td colspan="8" class="px-4 py-16 text-center text-n-slate-11">
              <span class="block mx-auto mb-2 size-9 i-lucide-package-search" />
              {{ t('ORDERS.EMPTY') }}
            </td>
          </tr>
          <tr
            v-for="order in visibleOrders"
            :key="order.key"
            class="transition-colors border-b border-l-4 cursor-pointer border-n-weak hover:bg-n-alpha-1"
            :class="SOURCES[order.source].border"
            @click="openOrder(order)"
          >
            <td class="px-4 py-3 font-medium sm:px-6 text-n-slate-12">
              {{ order.orderNo }}
              <span
                class="block size-2 mt-1 rounded-full sm:hidden"
                :class="SOURCES[order.source].dot"
              />
            </td>
            <td class="px-4 py-3">
              <div class="text-n-slate-12">{{ order.customerName || '—' }}</div>
              <div class="text-xs text-n-slate-10">
                {{ order.phone || '—' }}
              </div>
            </td>
            <td class="px-4 py-3">
              <span
                v-if="order.agentName"
                class="inline-flex items-center gap-1.5 text-n-slate-12"
              >
                <span class="size-3.5 i-lucide-user-round text-n-slate-10" />
                {{ order.agentName }}
              </span>
              <span v-else class="text-xs text-n-slate-10">
                {{ t('ORDERS.AGENT_EMPTY') }}
              </span>
            </td>
            <td class="px-4 py-3 font-medium text-n-slate-12 whitespace-nowrap">
              {{ formatCurrency(order.amount, order.currency) }}
            </td>
            <td
              class="hidden px-4 py-3 sm:table-cell text-n-slate-11 whitespace-nowrap"
            >
              {{ formatDate(order.date) }}
            </td>
            <td class="px-4 py-3">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="STATUS_BADGE_CLASSES[order.statusBucket]"
              >
                {{ order.statusLabel || '—' }}
              </span>
            </td>
            <td class="hidden px-4 py-3 lg:table-cell text-n-slate-11">
              {{ order.paymentType || '—' }}
            </td>
            <td class="hidden px-4 py-3 sm:table-cell sm:px-6">
              <span
                class="inline-flex items-center gap-1.5 text-xs font-medium"
                :class="SOURCES[order.source].badge.split(' ')[1]"
              >
                <span
                  class="rounded-full size-2"
                  :class="SOURCES[order.source].dot"
                />
                {{ SOURCES[order.source].label }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Sayfalama: derin arama sonuçlarında gösterilmez (tek seferlik liste) -->
    <footer
      v-if="!isDeepMode && !isLoading"
      class="flex items-center justify-between gap-3 px-4 py-3 border-t sm:px-6 border-n-weak flex-shrink-0"
    >
      <div class="flex items-center gap-2 text-xs text-n-slate-11">
        <span>{{ t('ORDERS.PAGINATION.PER_PAGE') }}</span>
        <select
          v-model.number="pageSize"
          class="w-20 px-2 py-1 border rounded-md bg-n-alpha-1 border-n-weak text-n-slate-12"
        >
          <option v-for="size in PAGE_SIZES" :key="size" :value="size">
            {{ size }}
          </option>
        </select>
      </div>
      <div class="flex items-center gap-2">
        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-n-slate-11 outline outline-1 outline-n-weak hover:bg-n-alpha-1 disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="page <= 1 || isRefreshing"
          @click="goToPage(page - 1)"
        >
          <span class="size-4 i-lucide-chevron-left" />
          {{ t('ORDERS.PAGINATION.PREV') }}
        </button>
        <span class="flex items-center gap-2 text-sm text-n-slate-11">
          <span
            v-if="isRefreshing"
            class="size-3.5 border-2 rounded-full animate-spin border-n-slate-8 border-t-transparent"
          />
          {{ t('ORDERS.PAGINATION.PAGE_OF', { page, pages: totalPages }) }}
        </span>
        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-n-slate-11 outline outline-1 outline-n-weak hover:bg-n-alpha-1 disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="page >= totalPages || isRefreshing"
          @click="goToPage(page + 1)"
        >
          {{ t('ORDERS.PAGINATION.NEXT') }}
          <span class="size-4 i-lucide-chevron-right" />
        </button>
      </div>
    </footer>
  </div>
</template>
